<!-- Localized -->
<template>
  <ModalContent
    ref="status-permissions-content"
    :name="name"
    :title="$t('pages.workflow_engine.groups')"
    :submit-button-text="$t('pages.workflow_engine.done')"
    width="1100px"
    v-bind="$attrs"
    v-on="$listeners"
    @submit="onSubmit"
    @hidden="onContentHide"
    :disabled="disabled"
  >
    <template #content>
      <Loader v-if="loading" />
      <div v-else class="grid grid-cols-2 auto-rows-fr gap-8 w-full">
        <GroupListEditView
          :title="$t('pages.workflow_engine.client_groups')"
          :list="filteredClientGroupsViewOrEdit"
          :disabled="disabled"
          show-select-all
          show-search
          class="h-full flex-1"
          @select="onGroupSelect"
          @search="handleFilterResult($event, 'client')"
        />

        <ListEditView
          selectKey="selected"
          valueKey="title"
          :title="$t('pages.workflow_engine.tenant_groups')"
          :listItems="filteredTenantGroupsEditOrView"
          :disableCheckbox="disabled"
          show-select-all
          show-search
          class="h-full overflow-y-auto"
          @handleSelect="selectAllTenantGroups"
          @rowClick="onRowClick"
          @handleFilterResult="handleFilterResult($event, 'tenant')"
        >
          <template #listheader>
            <div class="flex justify-between px-3 py-2 bg-gray-100 font-semibold">
              <div class="flex-1 font-semibold">
                <span>{{ $t('pages.workflow_engine.tenant_groups') }}</span>
              </div>
              <div class="flex justify-between gap-6">
                <div class="flex">
                  View
                </div>
                <div class="flex">
                  Edit
                </div>
              </div>
            </div>
          </template>
          <template #item="{ item }">
            <div class="flex justify-between">
              <div class="flex-1>">
                {{ item.title }}
              </div>
              <div class="flex justify-between w-2/12">
                <div class="flex">
                  <input
                    :checked="getRead(item)"
                    type="checkbox"
                    :disabled="disabled"
                    @input.stop="(value) => rowClickView(item, value)"
                    :id="item.id"
                    class="checkbox cursor-pointer checkbox-primary border-box-stroke checkbox-sm flex"
                  />
                </div>
                <div class="flex">
                  <input
                    :checked="getWrite(item)"
                    type="checkbox"
                    :disabled="disabled"
                    @input.stop="(value) => rowClickEdit(item, value)"
                    :id="item.id"
                    class="checkbox cursor-pointer checkbox-primary border-box-stroke checkbox-sm flex"
                  />
                </div>
              </div>
            </div>
          </template>
        </ListEditView>
      </div>
    </template>
  </ModalContent>
</template>

<script>
import ModalContent from "@shared/modal-content";
import ListEditView from "@shared/components/packages-form-edit-table/components/ListEditView.vue";
import GroupListEditView from "./GroupListEditView.vue";
// import ListView from "@shared/components/packages-form-edit-table/components/ListEditView.vue";
// import GroupedList from "@shared/components/packages-form-edit-table/components/GroupListEditView.vue";
import { sortItemsAlphabetically } from "@shared/utils/functions";
import { recursiveSearchFilter } from "@shared/utils/filters";
import Loader from "@shared/components/WrappedLoader";

export default {
  name: "GroupsModal",

  components: {
    ModalContent,
    ListEditView,
    GroupListEditView,
    Loader,
  },

  props: {
    /**
     * Holds all tenant and client groups in two arrays
     * @property {Object[]} tenant
     * @property {Object[]} client
     */
    groups: {
      type: Object,
      required: true,
    },
    /**
     * Holds an array of assigned group ids (tenant and client)
     * By default all groups are assigned
     */
    value: {
      type: Array,
      required: true,
    },
    valueEditOrView: {
      type: Array,
      required: true,
    },
    name: {
      type: String,
      default: "status-permissions-content",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      searchValues: {
        tenant: "",
        client: "",
      },
    };
  },

  computed: {
    filteredTenantGroups() {
      return this.tenantGroups.filter((el) =>
        el.title
          ?.toLowerCase()
          ?.includes(this.searchValues.tenant.toLowerCase())
      );
    },

    filteredTenantGroupsEditOrView() {
      return this.tenantGroupsEditOrView.filter((el) =>
        el.title
          ?.toLowerCase()
          ?.includes(this.searchValues.tenant.toLowerCase())
      );
    },

    tenantGroups() {
      return (
        this.groups.tenant
          ?.map((el) => ({ ...el, selected: this.value.includes(el.id) }))
          ?.sort((a, b) => sortItemsAlphabetically([a, b], "title"))
          ?.sort((a, b) => b.selected - a.selected) ?? []
      );
    },

    tenantGroupsEditOrView() {
      return (
        this.groups.tenant
          ?.map((el) => ({
            ...el,
            read: this.getRead(el),
            write: this.getWrite(el),
          }))
          ?.sort((a, b) => sortItemsAlphabetically([a, b], "title"))
          ?.sort((a, b) => b.selected - a.selected) ?? []
      );
    },

    filteredClientGroups() {
      return recursiveSearchFilter(
        this.clientGroups || [],
        this.searchValues.client,
        {
          filterKey: "name",
          listKey: "items",
        }
      );
    },

    filteredClientGroupsViewOrEdit() {
      return recursiveSearchFilter(
        this.clientGroupsViewOrEdit || [],
        this.searchValues.client,
        {
          filterKey: "name",
          listKey: "items",
        }
      );
    },

    clientGroups() {
      return this.groups.client
        .map((client) => ({
          id: client.client_id,
          name: client.client_name,
          items: client.groups.map((group) => ({
            id: group.group_id,
            name: group.group_name,
            selected: this.value.includes(group.group_id),
          })),
        }))
        .sort((a, b) => sortItemsAlphabetically([a, b], "name"))
        .sort(
          (a, b) =>
            b.items.every((el) => el.selected) -
            a.items.every((el) => el.selected)
        )
        .sort(
          (a, b) =>
            b.items.some((el) => el.selected) -
            a.items.some((el) => el.selected)
        );
    },

    clientGroupsViewOrEdit() {
      return this.groups.client
        .map((client) => ({
          id: client.client_id,
          name: client.client_name,
          items: client.groups.map((group) => ({
            id: group.group_id,
            name: group.group_name,
            read: this.getReadClient(group),
            write: this.getWriteClient(group),
          })),
        }))
        .sort((a, b) => sortItemsAlphabetically([a, b], "name"));
      // .sort((a, b) => b.items.every(el => el.selected) - a.items.every(el => el.selected))
      // .sort((a, b) => b.items.some(el => el.selected) - a.items.some(el => el.selected));
    },
  },

  methods: {
    getRead(el) {
      const found = this.valueEditOrView?.find((item) => item.id === el.id);
      return found ? !!found.read : false;
    },
    getWrite(el) {
      const found = this.valueEditOrView?.find((item) => item.id === el.id);
      return found ? !!found.write : false;
    },
    getReadClient(el) {
      const found = this.valueEditOrView?.find(
        (item) => item.id === el.group_id
      );
      return found ? !!found.read : false;
    },
    getWriteClient(el) {
      const found = this.valueEditOrView?.find(
        (item) => item.id === el.group_id
      );
      return found ? !!found.write : false;
    },
    handleFilterResult(event, type) {
      this.searchValues[type] = event;
    },
    rowClickEdit(item, event) {
      const isChecked = event.target.checked;

      const updated = this.valueEditOrView.map((obj) => {
        if (obj.id === item.id) {
          return {
            ...obj,
            write: isChecked,
            read: isChecked ? true : obj.read, // auto-check read if write is checked
          };
        }
        return obj;
      });

      this.$emit("input", updated);
    },
    rowClickView(item, event) {
      const isChecked = event.target.checked;

      const updated = this.valueEditOrView.map((obj) => {
        if (obj.id === item.id) {
          return {
            ...obj,
            read: isChecked,
            write: !isChecked ? false : obj.write, // auto-uncheck write if read is unchecked
          };
        }
        return obj;
      });

      this.$emit("input", updated);
    },
    // rowClickEdit(item, event) {
    //   // event.target.checked gives the new value for write
    //   const updated = this.valueEditOrView.map((obj) =>
    //     obj.id === item.id ? { ...obj, write: event.target.checked } : obj
    //   );
    //   this.$emit("input", updated);
    // },

    // rowClickView(item, event) {
    //   // event.target.checked gives the new value for read
    //   const updated = this.valueEditOrView.map((obj) =>
    //     obj.id === item.id ? { ...obj, read: event.target.checked } : obj
    //   );
    //   this.$emit("input", updated);
    // },
    // rowClickEdit(item, value) {
    //   console.log("rowClickEdit", value, item);
    //   // let payload = {
    //   //   row: item,
    //   // };
    //   // if (value) {
    //   //   payload = { ...payload, value: value.target.checked };
    //   // }
    //   // if (this.toggleKey) {
    //   //   payload.toggle = item[this.toggleKey];
    //   // }
    //   // this.onRowClick(payload);
    //   // this.$emit("rowClick", payload);
    // },
    // rowClickView(item, value) {
    //   console.log("rowClickView", value, item);
    //   // let payload = {
    //   //   row: item,
    //   // };
    //   // if (value) {
    //   //   payload = { ...payload, value: value.target.checked };
    //   // }
    //   // if (this.toggleKey) {
    //   //   payload.toggle = item[this.toggleKey];
    //   // }
    //   // this.onRowClick(payload);
    //   // this.$emit("rowClick", payload);
    // },
    onRowClick({ row }) {
      this.$emit("input", row.id);
    },
    onGroupSelect(payload) {
      // Directly emit only the updated items received in the payload
      this.$emit("input", payload.items);
    },
    onSubmit() {
      this.$emit("onSubmit");
      this.onContentHide();
    },
    // selectAllTenantGroups(event) {
    //   this.$emit("input", event ? this.groups.tenant.map(el => el.id) : []);
    // },
    selectAllTenantGroups(event) {
        this.$emit("input", event);
    },
    onContentHide() {
      this.searchValues.tenant = "";
      this.searchValues.client = "";
    },
    showModal() {
      this.$refs["status-permissions-content"]?.showModal();
    },
    hideModal() {
      this.$refs["status-permissions-content"]?.hideModal();
    },
  },
};
</script>
